import { AsgardDocBase } from "src/app/core/documents/asgard-doc-base";
import { DocumentManagerService } from "../document/document-manager.service";
import { AsgardDocEpub } from "src/app/core/documents/asgard-doc-epub";
import { IReaderPublication, IReaderView, IReaderViewAnnotationLayer } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { InjectionToken } from "@angular/core";
import { ILocator, ISimpleLocatorData, Locator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { SelectorFactory } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-base";
import { EpubCfiFragmentSelectorParser } from "@colibrio/colibrio-reader-framework/colibrio-core-selector-epubcfi";
import { CommandInvokerService } from "../command/command-invoker.service";
import { EpubAnnotationCommand } from "../command/epub-commands/epub-annotation.command";

export const EPubAnnotationServiceToken = new InjectionToken<IEpubAnnotationService>('EpubAnnotationService');

export interface IPersistedEpubAnnotation {
    layer: string;
    locator: ISimpleLocatorData;
    customData: any;
}

export interface IEpubAnnotations {
    bookId: string;
    annotations: IPersistedEpubAnnotation[];
}

export abstract class BaseEpubAnnotationService<T> implements IEpubAnnotationService {
    constructor(
        protected readonly documentManagerService: DocumentManagerService,
        private readonly commandInvoker: CommandInvokerService
    ) { }

    public readonly abstract LAYER_NAME: string;

    public async exportAnnotations(docId: string): Promise<IPersistedEpubAnnotation[]> {
        const layer = this.getAnnotationLayerByDocId(docId);
        if (!layer) {
            return [];
        }
        const out: IPersistedEpubAnnotation[] = [];
        for (const ann of layer.getAnnotations()) {
            if (!ann.getLocator()) {
                continue;
            }
            out.push({
                layer: this.LAYER_NAME,
                locator: ann.getLocator()!.toJSON(),
                customData: ann.getCustomData()
            });
        }
        return out;
    }

    public importAnnotation(annotation: IPersistedEpubAnnotation): void {
        if (annotation.layer !== this.LAYER_NAME || !this.readerView) return;

        const locator = this.mapToLocator(annotation.locator);
        this.createAnnotation(locator, annotation.customData as T);
    }

    protected abstract createAnnotation(locator: ILocator, data: T);

    protected getAnnotationLayerByDocId(docId: string) {
        const doc = this.documentManagerService.getDocumentById(docId);
        if (!this.isEpubDocAndReady(doc)) return undefined;

        return (doc as AsgardDocEpub).epubViewerInstance?.readerView.getAnnotationLayerByName(this.LAYER_NAME);
    }

    protected get readerView(): IReaderView | undefined {
        const currDoc = this.documentManagerService.currentDoc;
        if (!this.isEpubDocAndReady(currDoc)) return undefined;

        return (currDoc as AsgardDocEpub).epubViewerInstance?.readerView;
    }

    protected get annotationLayer(): IReaderViewAnnotationLayer<T> | null | undefined {
        const currDoc = this.documentManagerService.currentDoc;
        if (!this.isEpubDocAndReady(currDoc)) return undefined;

        return (currDoc as AsgardDocEpub).epubViewerInstance?.readerView.getAnnotationLayerByName(this.LAYER_NAME);
    }

    protected get readerPublication(): IReaderPublication | undefined {
        const currDoc = this.documentManagerService.currentDoc;
        if (!this.isEpubDocAndReady(currDoc)) return undefined;

        return (currDoc as AsgardDocEpub).epubViewerInstance?.readerPublication;
    }

    protected get docId(): string | undefined {
        return this.documentManagerService.currentDoc?.documentId;
    }

    protected isEpubDocAndReady(doc: AsgardDocBase | null | undefined): boolean {
        return !!doc && (doc instanceof AsgardDocEpub) && doc.isDocumentReady;
    }

    protected mapToLocator(locatorData: ISimpleLocatorData) {
        const selectorFactory = new SelectorFactory([
            new EpubCfiFragmentSelectorParser()
        ]);
        let selectors = locatorData.selectors.map(s => selectorFactory.createFromFragment(s)!);
        return new Locator(locatorData.sourceUrl, selectors);
    }

    protected async emitDocChanged(docId: string): Promise<void> {
        const doc = this.documentManagerService.getDocumentById(docId);
        if (!this.isEpubDocAndReady(doc)) return;
    
        const command = new EpubAnnotationCommand(doc as AsgardDocEpub);
        await this.commandInvoker.ExecuteCommand(command);
    }
}

export interface IEpubAnnotationService {
    readonly LAYER_NAME: string;
    exportAnnotations(docId: string): Promise<IPersistedEpubAnnotation[]>;
    importAnnotation(annotation: IPersistedEpubAnnotation): void;
}